/**
 * The home page's service navigator: a category list on the left, the selected
 * category's detail on the right. This is the mock-up's "you navigate by need,
 * not by our org chart" section.
 *
 * MODIFICATION (requested): the panel's closing CTA reads "Umów konsultację" and
 * leads to the contact page, replacing the mock-up's "Zobacz usługi w kategorii".
 * The mock-up also had a small duplicate "Umów konsultację" chip in the panel
 * header; it is dropped, because keeping both would put the same CTA twice in one
 * card. The route into the category is still one click away — from the category
 * row itself, the mega menu, and the catalogue page.
 *
 * The category list is a real tablist: arrow keys move between categories and
 * the panel is wired with aria-controls/aria-labelledby.
 */
import { clear, el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { ServiceCategory } from "../data/types.js";
import { eyebrow } from "./common.js";

export function serviceNavigator(ctx: AppContext, categories: ServiceCategory[]): HTMLElement {
	const copy = ctx.copy();
	if (categories.length === 0) return el("div");

	let activeIndex = 0;

	const tabs = el("div", {
		class: "mb-navigator__tabs",
		role: "tablist",
		"aria-label": copy.a11y.servicesMenu,
		"aria-orientation": "vertical",
	});
	const panel = el("div", {
		class: "mb-navigator__panel",
		role: "tabpanel",
		id: "mb-navigator-panel",
		tabindex: "0",
	});

	const tabButtons: HTMLElement[] = categories.map((category, index) =>
		el(
			"button",
			{
				class: "mb-navigator__tab",
				type: "button",
				role: "tab",
				id: `mb-navigator-tab-${category.slug}`,
				"aria-selected": String(index === 0),
				"aria-controls": "mb-navigator-panel",
				tabindex: index === 0 ? "0" : "-1",
				onClick: () => select(index),
			},
			el(
				"span",
				{ class: "mb-navigator__num" },
				category.icon ?? String(index + 1).padStart(2, "0"),
			),
			el("span", { class: "mb-navigator__tabtitle" }, category.title),
			el("span", { class: "mb-navigator__arrow", "aria-hidden": "true" }, "→"),
		),
	);
	for (const button of tabButtons) tabs.appendChild(button);

	tabs.addEventListener("keydown", (event) => {
		const key = event.key;
		const delta = key === "ArrowDown" ? 1 : key === "ArrowUp" ? -1 : 0;
		if (delta === 0) {
			if (key === "Home") select(0, true);
			else if (key === "End") select(categories.length - 1, true);
			else return;
			event.preventDefault();
			return;
		}
		event.preventDefault();
		const next = (activeIndex + delta + categories.length) % categories.length;
		select(next, true);
	});

	function select(index: number, moveFocus = false): void {
		activeIndex = index;
		tabButtons.forEach((button, i) => {
			const selected = i === index;
			button.setAttribute("aria-selected", String(selected));
			button.setAttribute("tabindex", selected ? "0" : "-1");
			button.classList.toggle("is-active", selected);
		});
		if (moveFocus) tabButtons[index]?.focus();
		renderPanel(index);
	}

	function renderPanel(index: number): void {
		const category = categories[index];
		if (!category) return;
		clear(panel);
		panel.setAttribute("aria-labelledby", `mb-navigator-tab-${category.slug}`);

		const services = el("ul", { class: "mb-navigator__services" });
		for (const service of category.services) {
			services.appendChild(
				el(
					"li",
					{ class: "mb-navigator__serviceitem" },
					el("span", { class: "mb-navigator__marker", "aria-hidden": "true" }, "✦"),
					el(
						"a",
						{
							class: "mb-navigator__service",
							href: ctx.href("serviceDetail", { slug: service.slug }),
						},
						service.title,
					),
				),
			);
		}

		panel.appendChild(
			el(
				"span",
				{ class: "mb-pill mb-pill--accent mb-navigator__badge" },
				`${copy.home.navigatorBadge} ${category.icon ?? String(index + 1).padStart(2, "0")}`,
			),
		);
		panel.appendChild(el("h3", { class: "mb-navigator__title" }, category.title));
		panel.appendChild(el("p", { class: "mb-navigator__lead" }, category.summary));
		panel.appendChild(services);
		panel.appendChild(
			el(
				"div",
				{ class: "mb-navigator__foot" },
				el(
					"div",
					{ class: "mb-navigator__stat" },
					el("span", { class: "mb-navigator__statvalue" }, String(category.services.length)),
					el("span", { class: "mb-navigator__statlabel" }, copy.home.navigatorStatLabel),
				),
				// MODIFICATION: consultation CTA in place of "see services in category".
				el(
					"a",
					{
						class: "mb-cta mb-cta--accent",
						href: ctx.href("contact"),
						"data-routekey": "contact",
						onClick: () =>
							ctx.track({
								type: "cta_click",
								cta: "navigator_consult",
								category: category.slug,
							}),
					},
					copy.home.navigatorConsult,
				),
			),
		);
	}

	renderPanel(0);
	tabButtons[0]?.classList.add("is-active");

	return el(
		"div",
		{ class: "mb-navigator", id: "nawigator" },
		el(
			"div",
			{ class: "mb-navigator__head" },
			eyebrow(copy.home.navigatorEyebrow),
			el("h2", { class: "mb-navigator__heading" }, copy.home.navigatorTitle),
			el("p", { class: "mb-navigator__headlead" }, copy.home.navigatorLead),
		),
		el("div", { class: "mb-navigator__body" }, tabs, panel),
	);
}
